import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Activity, TrendingUp, Zap, Info, BarChart2, Eye } from 'lucide-react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Bar,
  BarChart,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { apiClient } from '../lib/api-client'

type WeeklySalesRow = { week_start: string; revenue: number; units: number }

type TiktokRow = {
  date: string; plays: number; likes: number;
  comments: number; shares: number; videos: number; engagement: number
}

type SocialHaloResponse = {
  buzz: { date: string; value: number }[]
  buzzSources: string[]
  raw_tiktok: TiktokRow[]
  platforms: {
    amazon: WeeklySalesRow[]
    shopify: WeeklySalesRow[]
    ulta: WeeklySalesRow[]
    macys: WeeklySalesRow[]
    total: WeeklySalesRow[]
  }
  correlations: Record<
    'amazon' | 'shopify' | 'ulta' | 'macys' | 'total',
    { lagDays: number; r: number | null; pairedPoints: number }
  >
  meta: { buzzDays: number; days: number; tiktokNote?: string }
}

type PlatformKey = 'total' | 'amazon' | 'shopify' | 'ulta' | 'macys'
type TabKey = 'correlation' | 'tiktok_raw' | 'methodology'

const PLATFORM_LABELS: Record<PlatformKey, string> = {
  total: 'All Platforms',
  amazon: 'Amazon',
  shopify: 'Shopify (DTC)',
  ulta: 'Ulta Beauty',
  macys: "Macy's",
}

const PLATFORM_COLORS: Record<PlatformKey, string> = {
  total: '#8b5cf6',
  amazon: '#f97316',
  shopify: '#22c55e',
  ulta: '#ec4899',
  macys: '#3b82f6',
}

function fmtDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function fmtDateLong(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtRevenue(v: number) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v.toFixed(0)}`
}

function fmtNum(v: number) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`
  return `${v}`
}

function rStrength(r: number | null): { label: string; color: string; bg: string; border: string } {
  if (r === null) return { label: 'No data', color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' }
  const abs = Math.abs(r)
  const dir = r >= 0 ? 'Positive' : 'Negative'
  if (abs >= 0.8) return { label: `${dir} · Very Strong`, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' }
  if (abs >= 0.6) return { label: `${dir} · Strong`, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' }
  if (abs >= 0.4) return { label: `${dir} · Moderate`, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' }
  if (abs >= 0.2) return { label: `${dir} · Weak`, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' }
  return { label: 'No correlation', color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' }
}

function shiftDate(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export default function SocialHalo() {
  const [platform, setPlatform] = useState<PlatformKey>('shopify')
  const [tab, setTab] = useState<TabKey>('correlation')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['social-halo', 90],
    queryFn: () => apiClient.get<SocialHaloResponse>('/retail/social-halo?days=180'),
    staleTime: 5 * 60 * 1000,
  })

  const corr = data?.correlations[platform]
  const strength = rStrength(corr?.r ?? null)

  // Build correlation chart: buzz (lag-adjusted) vs weekly revenue
  const corrChartData = useMemo(() => {
    if (!data) return []
    const sales = data.platforms[platform]
    if (!sales.length) return []
    const buzzByDate = new Map(data.buzz.map(b => [b.date, b.value]))
    const lagDays = corr?.lagDays ?? 0

    return sales.map(s => {
      const window: number[] = []
      for (let i = 0; i < 7; i++) {
        const key = shiftDate(s.week_start, -(lagDays + i))
        const v = buzzByDate.get(key)
        if (v !== undefined) window.push(v)
      }
      const buzzAvg = window.length > 0
        ? parseFloat((window.reduce((a, b) => a + b, 0) / window.length).toFixed(2))
        : null
      return { date: s.week_start, buzz: buzzAvg, revenue: s.revenue }
    })
  }, [data, platform, corr?.lagDays])

  // TikTok raw data sorted newest-first for the table
  const tiktokTableData = useMemo(() => {
    if (!data?.raw_tiktok) return []
    return [...data.raw_tiktok]
      .filter(r => r.plays > 0 || r.likes > 0)
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 60)
  }, [data])

  // TikTok chart data (last 60 days, newest-first reversed for chart)
  const tiktokChartData = useMemo(() => {
    return [...tiktokTableData].reverse().slice(-30)
  }, [tiktokTableData])

  // Best correlation across platforms
  const bestPlatform = useMemo(() => {
    if (!data) return null
    let best: { key: PlatformKey; r: number; lagDays: number } | null = null
    for (const [k, v] of Object.entries(data.correlations) as [PlatformKey, typeof data.correlations.amazon][]) {
      if (v.r !== null && v.pairedPoints >= 4 && (best === null || Math.abs(v.r) > Math.abs(best.r))) {
        best = { key: k, r: v.r, lagDays: v.lagDays }
      }
    }
    return best
  }, [data])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Social Halo Analysis</h1>
          <p className="text-sm text-muted-foreground mt-1">Does brand buzz drive retail sales?</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse">
          {[1, 2, 3].map(i => <div key={i} className="bg-card border border-border rounded-xl p-4 h-24" />)}
        </div>
        <div className="bg-card border border-border rounded-xl p-6 animate-pulse">
          <div className="h-96 rounded-lg bg-muted/40" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold text-foreground">Social Halo Analysis</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <p className="text-red-700 font-medium">Failed to load data.</p>
          <p className="text-red-600 text-sm mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
          <button onClick={() => refetch()} className="mt-4 px-3 py-2 rounded-lg bg-red-100 border border-red-200 text-red-700 hover:bg-red-200 text-sm">Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Social Halo Analysis</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Measures whether TikTok &amp; Google brand buzz predicts retail revenue — and by how many days.
        </p>
      </div>

      {/* Data freshness notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-900 space-y-1">
          <p>
            <span className="font-semibold">Data coverage:</span>{' '}
            Google Trends gives <span className="font-medium">weekly</span> search-interest scores (0–100) for the past 180 days.
            TikTok is scraped via Apify for the <span className="font-medium">most recent ~200 videos</span> tagged{' '}
            <span className="font-mono">#buttahskin</span> / <span className="font-mono">#buttahskincare</span> — so older dates
            have sparser coverage. Run the scraper daily to build density over time.
          </p>
          <p>
            <span className="font-semibold">Correlation caveat:</span>{' '}
            Most platforms have only 4–9 paired data points — treat these as directional signals, not statistically conclusive.
            Confidence improves as weekly data accumulates.
          </p>
        </div>
      </div>

      {/* Headline callout */}
      {bestPlatform && bestPlatform.r !== null && Math.abs(bestPlatform.r) >= 0.4 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <Zap className="w-5 h-5 text-blue-600 shrink-0" />
          <p className="text-sm text-blue-900">
            <span className="font-semibold">{PLATFORM_LABELS[bestPlatform.key]}</span> shows the strongest signal{' '}
            <span className="font-mono font-semibold">(r={bestPlatform.r.toFixed(2)})</span>
            {bestPlatform.lagDays > 0 && (
              <span className="ml-1">
                — buzz spikes <span className="font-semibold">{bestPlatform.lagDays} days before</span> revenue moves.
              </span>
            )}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {([
          { key: 'correlation', label: 'Buzz → Revenue', icon: TrendingUp },
          { key: 'tiktok_raw', label: 'TikTok Raw Data', icon: BarChart2 },
          { key: 'methodology', label: 'How It Works', icon: Eye },
        ] as { key: TabKey; label: string; icon: React.ElementType }[]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === key
                ? 'border-white text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab: Buzz → Revenue ── */}
      {tab === 'correlation' && (
        <div className="space-y-5">
          {/* Correlation matrix */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Pearson r — Buzz vs Revenue (by Platform)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {(['amazon', 'shopify', 'ulta', 'macys', 'total'] as PlatformKey[]).map(key => {
                const c = data?.correlations[key]
                const s = rStrength(c?.r ?? null)
                const isActive = platform === key
                return (
                  <button
                    key={key}
                    onClick={() => setPlatform(key)}
                    className={`rounded-xl p-3 text-left transition-all border ${
                      isActive ? 'border-slate-400 bg-slate-100 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <p className="text-xs text-muted-foreground mb-1">{PLATFORM_LABELS[key]}</p>
                    <p className={`text-2xl font-mono font-bold ${s.color}`}>
                      {c?.r !== null && c?.r !== undefined ? c.r.toFixed(2) : '—'}
                    </p>
                    <p className={`text-xs mt-1 ${s.color}`}>{s.label}</p>
                    {c && c.lagDays > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">Best lag: {c.lagDays}d</p>
                    )}
                    <p className="text-xs text-muted-foreground/50 mt-0.5">n={c?.pairedPoints ?? 0} weeks</p>
                  </button>
                )
              })}
            </div>
            <div className="mt-3 space-y-2">
              <p className="text-xs text-slate-600">
                <span className="font-medium text-slate-700">How to read:</span>{' '}
                Pearson r ranges –1 to +1. Values closer to ±1 mean a stronger relationship between buzz and revenue.
                Click a platform card above to explore its chart.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <span className="text-green-700 font-semibold">Positive r (r &gt; 0)</span>
                  <p className="text-green-800 mt-0.5">Buzz and revenue rise together — viral spikes predict sales lifts, the ideal leading indicator.</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  <span className="text-orange-700 font-semibold">Negative r (r &lt; 0)</span>
                  <p className="text-orange-800 mt-0.5">Buzz spikes when revenue is low — often viral moments coincide with sell-outs or off-peak periods. Doesn't mean buzz hurts sales.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" style={{ color: PLATFORM_COLORS[platform] }} />
                  Brand Buzz vs {PLATFORM_LABELS[platform]} Revenue
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Buzz line = 7-day rolling avg of combined score, shifted {corr?.lagDays ?? 0} days earlier to align with best-fit lag.
                  Revenue = weekly net sales.
                </p>
              </div>
              <div className={`text-xs text-right rounded-lg px-3 py-2 ${strength.bg} border ${strength.border} space-y-0.5 max-w-[200px]`}>
                <p className={`font-semibold ${strength.color}`}>{strength.label}</p>
                <p className="text-slate-500">r = {corr?.r !== null && corr?.r !== undefined ? corr.r.toFixed(3) : '—'}</p>
                <p className="text-slate-500">lag = {corr?.lagDays ?? 0}d · n = {corr?.pairedPoints ?? 0}</p>
                {corr?.r !== null && corr?.r !== undefined && corr.r < 0 && (
                  <p className="text-orange-700 text-left mt-1 leading-tight">
                    Buzz peaks when sales dip — possible sell-out or off-peak viral effect.
                  </p>
                )}
              </div>
            </div>

            {/* Lag explanation */}
            {(corr?.lagDays ?? 0) > 0 && (
              <div className="mt-3 mb-4 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-slate-800 font-medium">What the lag means:</span>{' '}
                The chart tests buzz at offsets 0, 7, 14, 21 days before each revenue week and picks whichever produces
                the highest |r|. A {corr?.lagDays}d lag here means buzz from {corr?.lagDays} days ago correlates better with
                this week's sales than same-day buzz does — a potential leading indicator.
              </div>
            )}

            {corrChartData.length === 0 ? (
              <div className="h-96 flex items-center justify-center text-muted-foreground text-sm">
                No overlapping data for {PLATFORM_LABELS[platform]} in this window.
              </div>
            ) : (
              <div className="h-[420px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={corrChartData} margin={{ top: 10, right: 24, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={28} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis yAxisId="buzz" domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }}
                      label={{ value: 'Buzz (0–100)', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontSize: 11 } }} />
                    <YAxis yAxisId="rev" orientation="right" tickFormatter={fmtRevenue} tick={{ fontSize: 11, fill: '#64748b' }}
                      label={{ value: 'Weekly Revenue', angle: 90, position: 'insideRight', style: { fill: PLATFORM_COLORS[platform], fontSize: 11 } }} />
                    <Tooltip
                      labelFormatter={(l) => fmtDateLong(String(l))}
                      formatter={(value, name) => {
                        if (name === 'Brand Buzz') return [Number(value).toFixed(1), name]
                        return [`$${Number(value).toLocaleString()}`, name]
                      }}
                      contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                    />
                    <Legend />
                    <Line yAxisId="buzz" type="monotone" dataKey="buzz" name="Brand Buzz" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    <Line yAxisId="rev" type="monotone" dataKey="revenue" name={`${PLATFORM_LABELS[platform]} Revenue`}
                      stroke={PLATFORM_COLORS[platform]} strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: TikTok Raw Data ── */}
      {tab === 'tiktok_raw' && (
        <div className="space-y-5">
          {/* Engagement chart */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-pink-400" />
              Daily TikTok Engagement — Last 30 Days
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Engagement score = plays×1 + likes×5 + comments×3 + shares×2.
              Hashtags: <span className="font-mono">#buttahskin</span> + <span className="font-mono">#buttahskincare</span>.
              Each bar = all videos posted on that date.
            </p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={tiktokChartData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={14} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={fmtNum}
                    label={{ value: 'Engagement', angle: -90, position: 'insideLeft', style: { fill: '#64748b', fontSize: 10 } }} />
                  <Tooltip
                    labelFormatter={(l) => fmtDateLong(String(l))}
                    formatter={(v, name) => [fmtNum(Number(v)), name]}
                    contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  />
                  <Bar dataKey="engagement" name="Engagement Score" fill="#ec4899" opacity={0.85} radius={[3,3,0,0]} />
                  <Line dataKey="plays" name="Plays" stroke="#f97316" strokeWidth={1.5} dot={false} type="monotone" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Raw table */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Raw TikTok Data — Most Recent {tiktokTableData.length} Days
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="text-left py-2 pr-4">Date</th>
                    <th className="text-right pr-4">Videos</th>
                    <th className="text-right pr-4">Plays</th>
                    <th className="text-right pr-4">Likes</th>
                    <th className="text-right pr-4">Comments</th>
                    <th className="text-right pr-4">Shares</th>
                    <th className="text-right">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {tiktokTableData.map(r => (
                    <tr key={r.date} className="border-b border-border/40 hover:bg-white/5 transition-colors">
                      <td className="py-2 pr-4 text-muted-foreground font-mono text-xs">{r.date}</td>
                      <td className="text-right pr-4">{r.videos}</td>
                      <td className="text-right pr-4 text-orange-600">{fmtNum(r.plays)}</td>
                      <td className="text-right pr-4 text-pink-600">{fmtNum(r.likes)}</td>
                      <td className="text-right pr-4">{fmtNum(r.comments)}</td>
                      <td className="text-right pr-4">{fmtNum(r.shares)}</td>
                      <td className="text-right font-semibold text-purple-600">{fmtNum(r.engagement)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground/50 mt-3">
              Note: Apify scrapes the ~100 most recent videos per hashtag. Older dates may be missing plays/likes as
              videos fall off the scrape window. Run scraper daily to capture fresh data before it ages out.
            </p>
          </div>
        </div>
      )}

      {/* ── Tab: Methodology ── */}
      {tab === 'methodology' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-6 space-y-6 text-sm text-slate-600 leading-relaxed">

            <div>
              <h3 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-xs flex items-center justify-center font-bold">1</span>
                Buzz Score (0–100)
              </h3>
              <p>Two signals are combined into a single normalized <span className="text-slate-800 font-medium">Buzz Score</span> per day:</p>
              <ul className="mt-2 space-y-2 ml-4 list-disc">
                <li>
                  <span className="text-slate-800 font-medium">Google Trends search_interest</span> — weekly relative search volume
                  for "Buttah Skin" + "buttahskin" in the US, scored 0–100 where 100 = peak interest in the period.
                  Updated weekly; interpolated between data points.
                </li>
                <li>
                  <span className="text-slate-800 font-medium">TikTok engagement_score</span> — for all videos tagged
                  #buttahskin / #buttahskincare posted on each date:{' '}
                  <code className="bg-slate-100 border border-slate-200 px-1 rounded text-slate-800">
                    plays×1 + likes×5 + comments×3 + shares×2
                  </code>.
                  Likes are weighted 5× because they signal active intent vs passive views.
                  The raw score is normalized to 0–100 relative to the window's max.
                </li>
              </ul>
              <p className="mt-2">
                When both sources have data for a day, the Buzz Score is their average.
                When only one is available, that source is used alone.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 border border-purple-200 text-xs flex items-center justify-center font-bold">2</span>
                Lag Analysis
              </h3>
              <p>
                Social buzz doesn't move revenue instantly — there's a delay while awareness converts to purchase intent and then a sale.
                To find this lag, we test four offsets (0, 7, 14, 21 days) between the buzz window and each revenue week:
              </p>
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-4 font-mono text-xs text-slate-700 space-y-1">
                <p>For each offset D in [0, 7, 14, 21]:</p>
                <p className="ml-4">buzz_window = 7-day avg of buzz ending (revenue_week_start − D)</p>
                <p className="ml-4">pair = (buzz_window, weekly_revenue)</p>
                <p className="ml-4">r = Pearson correlation across all paired weeks</p>
                <p className="mt-2 font-semibold">Best lag = D with highest |r|</p>
              </div>
              <p className="mt-2">
                The <span className="text-slate-800 font-medium">Buzz → Revenue chart</span> shows buzz shifted left by the best lag,
                so you can visually confirm whether the lines move together.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 border border-green-200 text-xs flex items-center justify-center font-bold">3</span>
                Pearson r — What the Numbers Mean
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {[
                  { range: 'r ≥ 0.8', label: 'Very Strong positive', desc: 'Buzz reliably precedes revenue increases' },
                  { range: '0.6 – 0.8', label: 'Strong positive', desc: 'Clear directional signal, worth acting on' },
                  { range: '0.4 – 0.6', label: 'Moderate', desc: 'Directional but noisy — accumulate more data' },
                  { range: '0 – 0.4', label: 'Weak / no signal', desc: 'Likely coincidence at current sample size' },
                  { range: 'r < 0', label: 'Negative', desc: 'Buzz spikes when revenue is low — sell-outs or off-peak viral moments' },
                ].map(row => (
                  <div key={row.range} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <p className="text-slate-800 font-mono text-xs font-semibold">{row.range}</p>
                    <p className="text-slate-700 text-xs font-medium">{row.label}</p>
                    <p className="text-slate-500 text-xs">{row.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 border border-amber-200 text-xs flex items-center justify-center font-bold">4</span>
                Current Limitations
              </h3>
              <ul className="space-y-2 ml-4 list-disc">
                <li>
                  <span className="text-slate-800 font-medium">Small n:</span> Most platforms have 4–9 weekly revenue points.
                  Pearson r needs ~20+ points to be statistically meaningful. Treat current readings as directional.
                </li>
                <li>
                  <span className="text-slate-800 font-medium">TikTok recency bias:</span> Apify only scrapes the most recent ~100 videos
                  per hashtag. Older videos lose plays/likes over time and fall off the window.
                  The scraper should run daily to capture fresh metrics before they age.
                </li>
                <li>
                  <span className="text-slate-800 font-medium">Google Trends is weekly:</span> Daily granularity requires a paid API or
                  a narrower (&lt;30d) query window. Current data has ~20 weekly points for the 90-day window.
                </li>
                <li>
                  <span className="text-slate-800 font-medium">Confounders:</span> Promotions, seasonality, and influencer deals
                  affect both buzz and sales simultaneously — correlation doesn't prove causation.
                </li>
              </ul>
            </div>

          </div>
        </div>
      )}
    </div>
  )
}
